import {
  addDisplayName,
  addHandlers,
  addProps,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { ChangeEvent, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Trans } from 'react-i18next'
import cx from 'classnames'
import { getEnvVar } from 'shared/src/utils/env'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from 'components/Button'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { SUBSCRIBE_BUTTON_Z_INDEX } from 'utils/zIndex'
import lasso from '../../assets/subscribe-lasso.svg'
import { addNotificationContext } from 'components/notifications'

const KLAVIYO_COMPANY_ID = getEnvVar('KLAVIYO_COMPANY_ID')
const KLAVIYO_LIST_ID = getEnvVar('KLAVIYO_LIST_ID')

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    paddingTop: 50,
    paddingBottom: 100,
  },
  lasso: {
    position: 'absolute',
    left: -40,
    top: 0,
  },
  title: {
    marginLeft: 32,
    maxWidth: 149,
    marginBottom: 16,
    fontFamily: serifFontFamily,
    fontWeight: 600,
    fontSize: 22,
    lineHeight: '140%',
    color: 'rgba(255, 255, 255, 0.88)',
  },
  titleItalic: {
    fontStyle: 'italic',
  },
  description: {
    marginLeft: 56,
    maxWidth: 199,
    marginBottom: 38,
    fontFamily: sansSerifFontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  descriptionBold: {
    fontWeight: 700,
  },
  textFieldContainer: {
    marginLeft: 32,
    marginRight: 32,
    display: 'flex',
    height: 64,
  },
  textField: {
    flexGrow: 1,
    minWidth: 0,
    background: 'rgba(54, 52, 50, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.24)',
    borderRightWidth: 0,
    borderRadius: '2px 0 0 2px',
    paddingLeft: 16,
    paddingRight: 16,
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '26pc',
    letterSpacing: '0.2px',
    opacity: 0.8,
    color: 'rgba(255, 255, 255, 0.8)',
    '&:focus': {
      border: '1px solid rgba(255, 255, 255, 0.24)',
      outline: 'none',
    },
  },
  button: {
    zIndex: SUBSCRIBE_BUTTON_Z_INDEX,
    marginLeft: -2,
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    fontFamily: sansSerifFontFamily,
    fontWeight: 800,
    fontSize: 14,
    lineHeight: '17px',
    letterSpacing: '0.1em',
    color: 'white',
    paddingLeft: 20,
    paddingRight: 20,
    borderRadius: 2,
  },
})

interface Props {
  className?: string
}

export const Subscribe: FC<Props> = flowMax(
  addDisplayName('Subscribe'),
  addStateHandlers(
    {
      email: '',
    },
    {
      onChangeEmail: () => (event: ChangeEvent<HTMLInputElement>) => ({
        email: event.target.value,
      }),
    }
  ),
  addProps(({ email }) => ({
    disabled: email.length === 0,
  })),
  addNotificationContext,
  addTranslationHelpers,
  addHandlers({
    onSubmit:
      ({ email, showNotification, t }) =>
      async () => {
        await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
          {
            method: 'POST',
            headers: {
              revision: '2022-10-17',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  email,
                  list_id: KLAVIYO_LIST_ID,
                },
              },
            }),
          }
        )
        showNotification({
          text: t('notifications.subscribed'),
          style: 'top',
        })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ email, onChangeEmail, disabled, onSubmit, classes, className, t }) => (
    <div className={cx(classes.container, className)}>
      <img src={lasso} alt="" className={classes.lasso} />
      <div className={classes.title}>
        <Trans t={t} i18nKey="menu.subscribe.title">
          <span className={classes.titleItalic} />
        </Trans>
      </div>
      <div className={classes.description}>
        <Trans t={t} i18nKey="menu.subscribe.description">
          <span className={classes.descriptionBold} />
        </Trans>
      </div>
      <div className={classes.textFieldContainer}>
        <input
          type="email"
          placeholder={t('menu.subscribe.email')}
          value={email}
          onChange={onChangeEmail}
          className={classes.textField}
        />
        <Button
          onClick={onSubmit}
          disabled={disabled}
          className={classes.button}
        >
          {t('menu.subscribe.submit')}
        </Button>
      </div>
    </div>
  )
)
